import { resolve } from "path";
import {
  buildJobAdmissionErrorBody,
  isJobAdmissionError,
  type JobAdmissionErrorBody,
  type WakeAppResult,
} from "@gencow/core";

import { ensurePlatformRuntimeStateTable, getPlatformSlotActivity } from "./platform-active-slot";
import { processScheduledJobsPoll } from "./scheduled-jobs-poller";
import { applyScheduledJobAdmission } from "./job-admission-policy";
import { createSingleFlightWake } from "./wake-signal/single-flight-wake";
import { GENCOW_PLATFORM_WAKE_CHANNEL, buildWakeNotifySql } from "./wake-signal/wake-signal-types";
import type { WakeSignalBus } from "./wake-signal/postgres-wake-signal-bus";

type LoggerLike = Pick<Console, "log" | "warn" | "error">;
type RawSql = ((query: string, params?: unknown[]) => Promise<unknown>) & {
  transaction?: <T>(callback: (rawSql: RawSql) => Promise<T>) => Promise<T>;
};
type TimerHandle = ReturnType<typeof setTimeout>;

type ScheduledJobsProvisionerBridge = {
  getAppPort(appName: string): number | undefined | null;
  wakeApp(appName: string): Promise<WakeAppResult>;
  provisionApp(
    appName: string,
    port: number,
    dataDir: string,
    appDbId: number,
    isProd: boolean,
    reason: "wake",
  ): Promise<void>;
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
  generateCronToken(appId: number, appName: string): string;
  generateInternalToken(appId: number, appName: string): string;
};

type AppIdRow = { id: number };
type NextScheduledRunAtRow = { next_run_at?: unknown };
type ScheduledJobResponseBody = Record<string, unknown> | JobAdmissionErrorBody;

type SchedulerPersistBody = {
  appName: string;
  id: string;
  action: string;
  args: unknown;
  runAt: string;
  onErrorAction?: string;
  dedupeKey?: string | null;
};

type SchedulerCancelBody = {
  appName: string;
  id: string;
};

type RouteHandlerContext = {
  req: {
    json(): Promise<unknown>;
    header(name: string): string | undefined;
  };
  json(body: unknown, status?: number): unknown;
};

type RouteRegistrar = {
  post(path: string, handler: (context: RouteHandlerContext) => Promise<unknown> | unknown): void;
};

export const SCHED_POLL_INTERVAL = 10_000;
export const SCHED_MIN_POLL_INTERVAL = 1_000;
export const SCHED_MAX_POLL_INTERVAL = 300_000;
export const SCHED_MAX_JOBS_PER_POLL = 20;
export const SCHED_POLL_INTERVAL_ENV = "GENCOW_SCHEDULED_JOBS_POLL_INTERVAL_MS";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function validateSchedulerPersistBody(body: SchedulerPersistBody): string | null {
  return body.appName && body.id && body.action && body.runAt ? null : "appName, id, action, runAt required";
}

export function validateSchedulerCancelBody(body: SchedulerCancelBody): string | null {
  return body.appName && body.id ? null : "appName and id required";
}

function normalizeSchedulerArgs(args: unknown): unknown {
  if (typeof args !== "string") return args ?? {};
  try {
    return JSON.parse(args);
  } catch {
    return args;
  }
}

function clampPollIntervalMs(value: number): number {
  return Math.min(SCHED_MAX_POLL_INTERVAL, Math.max(SCHED_MIN_POLL_INTERVAL, value));
}

export function resolveScheduledJobsPollIntervalMs(
  env: Record<string, string | undefined> | undefined,
  override?: number,
): number {
  if (typeof override === "number" && Number.isFinite(override) && override > 0) {
    return override;
  }

  const rawValue = env?.[SCHED_POLL_INTERVAL_ENV];
  if (!rawValue) return SCHED_POLL_INTERVAL;

  const parsedValue = Number(rawValue);
  if (!Number.isInteger(parsedValue) || parsedValue <= 0) return SCHED_POLL_INTERVAL;
  return clampPollIntervalMs(parsedValue);
}

function resolveRunAtTimestampMs(value: unknown): number | null {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  if (typeof value === "string" || typeof value === "number") {
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  return null;
}

async function resolveSchedulerAppId(rawSql: RawSql, appName: string): Promise<number | null> {
  const appRows = (await rawSql(`SELECT id FROM apps WHERE name = $1`, [appName])) as AppIdRow[];
  return appRows[0]?.id ?? null;
}

async function runRawSqlTransaction<T>(
  rawSql: RawSql,
  callback: (txRawSql: RawSql) => Promise<T>,
): Promise<T> {
  if (rawSql.transaction) {
    return rawSql.transaction(callback);
  }
  return callback(rawSql);
}

async function notifySchedulerWake(rawSql: RawSql): Promise<void> {
  const statement = buildWakeNotifySql({
    kind: "scheduler",
    defaultChannel: GENCOW_PLATFORM_WAKE_CHANNEL,
  });
  await rawSql(statement.query, statement.params);
}

async function getNextScheduledRunAtMs(rawSql: RawSql): Promise<number | null> {
  const rows = (await rawSql(
    `
      SELECT MIN(sj.run_at) AS next_run_at
      FROM scheduled_jobs sj
      JOIN apps a ON a.id = sj.app_id
      WHERE sj.status = 'pending'
        AND sj.run_at > NOW()
        AND a.status IN ('running', 'idle')
    `,
  )) as NextScheduledRunAtRow[];
  return resolveRunAtTimestampMs(rows[0]?.next_run_at);
}

async function upsertScheduledJob(params: {
  rawSql: RawSql;
  appId: number;
  body: SchedulerPersistBody;
  normalizedArgs: unknown;
  existingJobId?: string;
  dedupeKey?: string;
}): Promise<string | null> {
  if (params.existingJobId) {
    const updated = (await params.rawSql(
      `UPDATE scheduled_jobs
          SET action = $3,
              args = $4::jsonb,
              run_at = $5,
              on_error_action = $6,
              status = 'pending',
              retry_count = 0,
              error = NULL,
              dedupe_key = $7,
              updated_at = NOW()
        WHERE id = $1
          AND app_id = $2
          AND status <> 'running'
        RETURNING id`,
      [
        params.existingJobId,
        params.appId,
        params.body.action,
        params.normalizedArgs,
        params.body.runAt,
        params.body.onErrorAction ?? null,
        params.dedupeKey ?? null,
      ],
    )) as Array<{ id: string }>;
    return updated[0]?.id ?? null;
  }

  const inserted = (await params.rawSql(
    `INSERT INTO scheduled_jobs (id, app_id, action, args, run_at, on_error_action, dedupe_key)
               VALUES ($1, $2, $3, $4::jsonb, $5, $6, $7)
               ON CONFLICT (id) DO UPDATE SET
                  action = EXCLUDED.action,
                  args = EXCLUDED.args,
                  run_at = EXCLUDED.run_at,
                  on_error_action = EXCLUDED.on_error_action,
                  dedupe_key = EXCLUDED.dedupe_key,
                  status = 'pending',
                  retry_count = 0,
                  error = NULL,
                  updated_at = NOW()
               WHERE scheduled_jobs.app_id = EXCLUDED.app_id
                 AND scheduled_jobs.status <> 'running'
               RETURNING id`,
    [
      params.body.id,
      params.appId,
      params.body.action,
      params.normalizedArgs,
      params.body.runAt,
      params.body.onErrorAction ?? null,
      params.dedupeKey ?? null,
    ],
  )) as Array<{ id: string }>;
  return inserted[0]?.id ?? null;
}

export async function persistScheduledJob(params: {
  rawSql: RawSql;
  generateInternalToken: (appId: number, appName: string) => string;
  token: string;
  body: SchedulerPersistBody;
  ensureActivePlatform?: () => Promise<boolean>;
  env?: Record<string, string | undefined>;
  now?: () => Date;
  logger?: LoggerLike;
}): Promise<{ status: number; body: ScheduledJobResponseBody }> {
  const logger = params.logger ?? console;
  const validationError = validateSchedulerPersistBody(params.body);
  if (validationError) {
    return { status: 400, body: { error: validationError } };
  }
  if (params.ensureActivePlatform && !(await params.ensureActivePlatform())) {
    return { status: 503, body: { error: "Inactive platform slot" } };
  }

  const appId = await resolveSchedulerAppId(params.rawSql, params.body.appName);
  if (!appId) {
    return { status: 404, body: { error: "App not found" } };
  }

  const expectedToken = params.generateInternalToken(appId, params.body.appName);
  if (params.token !== expectedToken) {
    logger.warn(`[scheduler] persist: invalid token for app ${params.body.appName}`);
    return { status: 403, body: { error: "Invalid token" } };
  }

  const normalizedArgs = normalizeSchedulerArgs(params.body.args);
  let persistedJobId: string | null = null;
  try {
    persistedJobId = await runRawSqlTransaction(params.rawSql, async (rawSql) => {
      const decision = await applyScheduledJobAdmission({
        rawSql,
        appId,
        appName: params.body.appName,
        jobId: params.body.id,
        action: params.body.action,
        args: normalizedArgs,
        runAt: params.body.runAt,
        dedupeKey: params.body.dedupeKey,
        env: params.env,
        now: params.now,
      });
      const upsertedJobId = await upsertScheduledJob({
        rawSql,
        appId,
        body: params.body,
        normalizedArgs,
        existingJobId: decision.existingJobId,
        dedupeKey: decision.dedupeKey,
      });
      if (upsertedJobId) {
        await notifySchedulerWake(rawSql).catch((error: unknown) => {
          logger.warn(`[scheduler] wake notify failed; fallback poll will repair: ${errorMessage(error)}`);
        });
      }
      return upsertedJobId;
    });
  } catch (error) {
    if (isJobAdmissionError(error)) {
      logger.warn(
        `[scheduler] persist: rejected ${params.body.appName}/${params.body.action} (${error.code})`,
      );
      return { status: error.status, body: buildJobAdmissionErrorBody(error) };
    }
    throw error;
  }

  if (!persistedJobId) {
    return { status: 409, body: { error: "Scheduled job already running or id belongs to another app" } };
  }

  return { status: 200, body: { ok: true, id: persistedJobId } };
}

export async function cancelScheduledJob(params: {
  rawSql: RawSql;
  generateInternalToken: (appId: number, appName: string) => string;
  token: string;
  body: SchedulerCancelBody;
  ensureActivePlatform?: () => Promise<boolean>;
  logger?: LoggerLike;
}): Promise<{ status: number; body: Record<string, unknown> }> {
  const logger = params.logger ?? console;
  const validationError = validateSchedulerCancelBody(params.body);
  if (validationError) {
    return { status: 400, body: { error: validationError } };
  }
  if (params.ensureActivePlatform && !(await params.ensureActivePlatform())) {
    return { status: 503, body: { error: "Inactive platform slot" } };
  }

  const appId = await resolveSchedulerAppId(params.rawSql, params.body.appName);
  if (!appId) {
    return { status: 404, body: { error: "App not found" } };
  }

  const expectedToken = params.generateInternalToken(appId, params.body.appName);
  if (params.token !== expectedToken) {
    logger.warn(`[scheduler] cancel: invalid token for app ${params.body.appName}`);
    return { status: 403, body: { error: "Invalid token" } };
  }

  const deleted = (await params.rawSql(
    `DELETE FROM scheduled_jobs WHERE id = $1 AND app_id = $2 RETURNING id`,
    [params.body.id, appId],
  )) as unknown[];

  return { status: 200, body: { ok: true, deleted: deleted.length > 0 } };
}

export async function registerScheduledJobsPoller(params: {
  functionsPath: string;
  rawSql: RawSql;
  cronExecTimeoutMs: number;
  logger?: LoggerLike;
  setIntervalImpl?: typeof setInterval;
  setTimeoutImpl?: typeof setTimeout;
  clearTimeoutImpl?: typeof clearTimeout;
  pollIntervalMs?: number;
  maxJobsPerPoll?: number;
  wakeSignalBus?: WakeSignalBus;
  env?: Record<string, string | undefined>;
  now?: () => Date;
  importProvisioner?: (path: string) => Promise<ScheduledJobsProvisionerBridge>;
}): Promise<void> {
  const logger = params.logger ?? console;
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const setTimeoutImpl = params.setTimeoutImpl ?? setTimeout;
  const clearTimeoutImpl = params.clearTimeoutImpl ?? clearTimeout;
  const now = params.now ?? (() => new Date());
  const pollIntervalMs = resolveScheduledJobsPollIntervalMs(params.env ?? process.env, params.pollIntervalMs);
  const maxJobsPerPoll = params.maxJobsPerPoll ?? SCHED_MAX_JOBS_PER_POLL;
  const importProvisioner =
    params.importProvisioner ?? ((path: string) => import(path) as Promise<ScheduledJobsProvisionerBridge>);

  const provisioner = await importProvisioner(resolve(params.functionsPath, "../src/provisioner.ts"));
  await ensurePlatformRuntimeStateTable(params.rawSql).catch((error: unknown) => {
    logger.warn(`[platform] Scheduled Jobs Poller active-slot table init failed: ${errorMessage(error)}`);
  });
  let lastActiveState: boolean | null = null;
  let nextDueTimer: TimerHandle | null = null;
  let runPoll: () => Promise<void>;

  function clearNextDueTimer(): void {
    if (!nextDueTimer) return;
    clearTimeoutImpl(nextDueTimer);
    nextDueTimer = null;
  }

  async function scheduleNextDueTimer(): Promise<void> {
    if (!params.wakeSignalBus) return;

    const nextRunAtMs = await getNextScheduledRunAtMs(params.rawSql).catch((error: unknown) => {
      logger.warn(`[platform] Scheduled Jobs Poller next-run lookup failed: ${errorMessage(error)}`);
      return null;
    });
    if (nextRunAtMs === null) {
      clearNextDueTimer();
      return;
    }

    const delayMs = nextRunAtMs - now().getTime();
    if (delayMs < 0 || delayMs >= pollIntervalMs) {
      clearNextDueTimer();
      return;
    }

    clearNextDueTimer();
    nextDueTimer = setTimeoutImpl(() => {
      nextDueTimer = null;
      runPoll().catch((error: unknown) => {
        logger.error("[scheduled] Next-due timer failed:", errorMessage(error));
      });
    }, delayMs);
  }

  runPoll = createSingleFlightWake(async () => {
    const activity = await getPlatformSlotActivity({ rawSql: params.rawSql, logger });
    if (!activity.active) {
      if (lastActiveState !== false) {
        logger.log(
          `[platform] Scheduled Jobs Poller paused on inactive slot :${activity.currentPort} (active :${activity.activePort})`,
        );
      }
      lastActiveState = false;
      clearNextDueTimer();
      return;
    }
    if (lastActiveState === false) {
      logger.log(
        `[platform] Scheduled Jobs Poller resumed on active slot :${activity.currentPort ?? "unknown"}`,
      );
    }
    lastActiveState = true;

    await processScheduledJobsPoll({
      rawSql: params.rawSql,
      getPort: provisioner.getAppPort,
      wakeApp: provisioner.wakeApp,
      provisionApp: provisioner.provisionApp,
      setAppMeta: provisioner.setAppMeta,
      generateCronToken: provisioner.generateCronToken,
      cronExecTimeoutMs: params.cronExecTimeoutMs,
      maxJobsPerPoll,
      logger,
    });
    await scheduleNextDueTimer();
  });

  setIntervalImpl(
    () => runPoll().catch((error: unknown) => {
      logger.error("[scheduled] Poller tick failed:", errorMessage(error));
    }),
    pollIntervalMs,
  );

  if (params.wakeSignalBus) {
    const runStartupScan = async () => {
      await runPoll().catch((error: unknown) => {
        logger.warn(`[platform] Scheduled Jobs Poller startup scan failed: ${errorMessage(error)}`);
      });
    };

    try {
      await params.wakeSignalBus.listen(
        ["scheduler"],
        async () => {
          await runPoll();
        },
        { onListen: runStartupScan },
      );
      logger.log("[platform] Scheduled Jobs Poller wake listener registered");
    } catch (error) {
      logger.warn(`[platform] Scheduled Jobs Poller wake listener disabled: ${errorMessage(error)}`);
    }
  }

  logger.log(`[platform] Scheduled Jobs Poller registered (${pollIntervalMs / 1000}s poll)`);
}

export function registerScheduledJobRoutes(params: {
  app: RouteRegistrar;
  rawSql: RawSql;
  generateInternalToken: (appId: number, appName: string) => string;
  logger?: LoggerLike;
}): void {
  const logger = params.logger ?? console;
  const ensureActivePlatform = async () =>
    (await getPlatformSlotActivity({ rawSql: params.rawSql, logger })).active;

  params.app.post("/internal/scheduler/persist", async (c) => {
    try {
      const result = await persistScheduledJob({
        rawSql: params.rawSql,
        generateInternalToken: params.generateInternalToken,
        token: c.req.header("X-Internal-Token") || "",
        body: (await c.req.json()) as SchedulerPersistBody,
        ensureActivePlatform,
        logger,
      });
      return c.json(result.body, result.status);
    } catch (e: unknown) {
      logger.error("[scheduler] persist error:", errorMessage(e));
      return c.json({ error: errorMessage(e) }, 500);
    }
  });

  params.app.post("/internal/scheduler/cancel", async (c) => {
    try {
      const result = await cancelScheduledJob({
        rawSql: params.rawSql,
        generateInternalToken: params.generateInternalToken,
        token: c.req.header("X-Internal-Token") || "",
        body: (await c.req.json()) as SchedulerCancelBody,
        ensureActivePlatform,
        logger,
      });
      return c.json(result.body, result.status);
    } catch (e: unknown) {
      logger.error("[scheduler] cancel error:", errorMessage(e));
      return c.json({ error: errorMessage(e) }, 500);
    }
  });
}
